/**
 * Abgleich mit einem Ordner auf dem PC (Windows-App).
 *
 * Der Ordner spiegelt alle Projekte: <Ordner>/<Projekt>/<Ordner>/<Unterordner>/<Datei>.
 * - Neue Dateien in HandVero werden heruntergeladen, umbenannte oder verschobene am PC nachgezogen.
 * - Neue Dateien und Unterordner, die man am PC in einen Projektordner legt, kommen nach HandVero.
 * - Eine am PC bearbeitete Datei ersetzt die Fassung in HandVero.
 * - In HandVero gelöschte Dateien wandern am PC in den Papierkorb des Abgleichs (.handvero/Papierkorb),
 *   nichts wird am PC endgültig gelöscht. Am PC gelöschte Dateien holt der nächste Abgleich zurück.
 *
 * Die Planung ist reine Logik und hier getestet; das Ausführen übernimmt die Web-App mit der Windows-App.
 */
import { ALLOWED_FILES, fileExtension, MAX_FILE_MB } from "./projects";

export interface SyncFolder {
  id: string;
  parentId: string | null;
  name: string;
}

export interface SyncFile {
  id: string;
  folderId: string;
  name: string;
  size: number;
  createdAt: string;
}

export interface SyncProject {
  id: string;
  name: string;
  folders: SyncFolder[];
  files: SyncFile[];
}

export interface SyncManifest {
  /** Firma und Person, zu denen der Ordner gehört; ein Ordner gehört immer genau einem Konto. */
  account: string;
  projects: SyncProject[];
}

export interface DownloadInfo {
  /** Ob für diese Version ein Windows-Installationsprogramm bereitliegt. */
  windows: boolean;
  fileName: string;
}

/** Was der Abgleich sich zu jeder Datei merkt, die er schon einmal abgeglichen hat. */
export interface SyncStateFile {
  path: string;
  size: number;
  mtime: number;
  projectId: string;
}

export interface SyncState {
  version: 1;
  account: string;
  files: Record<string, SyncStateFile>;
}

export interface LocalFile {
  /** Relativ zum gewählten Ordner, mit „/“ getrennt. */
  path: string;
  size: number;
  /** Letzte Änderung in Millisekunden. */
  mtime: number;
}

export interface LocalListing {
  files: LocalFile[];
  dirs: string[];
}

export type SyncAction =
  | { type: "download"; fileId: string; projectId: string; path: string; size: number }
  | { type: "move"; fileId: string; from: string; to: string }
  | { type: "trash"; fileId: string; path: string }
  | { type: "adopt"; fileId: string; projectId: string; path: string; size: number; mtime: number }
  | { type: "forget"; fileId: string }
  | { type: "upload"; projectId: string; folderId: string; path: string; size: number; mtime: number }
  | { type: "replace"; fileId: string; projectId: string; folderId: string; path: string; size: number; mtime: number }
  | { type: "createFolder"; projectId: string; parentId: string | null; name: string; path: string }
  | { type: "mkdir"; path: string };

export interface SyncSkip {
  path: string;
  reason: string;
}

export interface SyncPlan {
  actions: SyncAction[];
  skipped: SyncSkip[];
}

export const SYNC_DIR = ".handvero";

export function emptyState(account: string): SyncState {
  return { version: 1, account, files: {} };
}

const BAD_CHARS = /[<>:"/\\|?*\u0000-\u001f]/g;
const RESERVED = /^(con|prn|aux|nul|com[1-9]|lpt[1-9])(\..*)?$/i;

/** Macht aus einem Namen in HandVero einen Datei- oder Ordnernamen, den Windows erlaubt. */
export function safeName(name: string): string {
  let n = name.replace(BAD_CHARS, "_").trim().replace(/[. ]+$/, "");
  if (!n) n = "_";
  if (RESERVED.test(n)) n = `_${n}`;
  if (n.length > 120) {
    const ext = fileExtension(n);
    n = ext ? `${n.slice(0, 115 - ext.length).trimEnd()}.${ext}` : n.slice(0, 120).trimEnd();
  }
  return n;
}

/** „Bericht.pdf“ → „Bericht (2).pdf“ */
export function numbered(name: string, n: number): string {
  const ext = /\.[A-Za-z0-9]{1,8}$/.exec(name)?.[0] ?? "";
  return `${name.slice(0, name.length - ext.length)} (${n})${ext}`;
}

/** Windows unterscheidet nicht zwischen Groß- und Kleinschreibung. */
const key = (path: string) => path.toLowerCase();

function unique(name: string, taken: Set<string>, dir: string): string {
  let candidate = name;
  for (let n = 2; taken.has(key(join(dir, candidate))); n++) candidate = numbered(name, n);
  taken.add(key(join(dir, candidate)));
  return candidate;
}

function join(dir: string, name: string) {
  return dir ? `${dir}/${name}` : name;
}

function dirname(path: string) {
  const i = path.lastIndexOf("/");
  return i < 0 ? "" : path.slice(0, i);
}

function basename(path: string) {
  return path.slice(path.lastIndexOf("/") + 1);
}

export interface SyncLayout {
  /** Projekt-ID je Projektordner (Schlüssel klein geschrieben). */
  projectDirs: Map<string, { projectId: string; path: string }>;
  /** Ordner je Pfad (Schlüssel klein geschrieben). */
  folders: Map<string, { projectId: string; folderId: string; path: string }>;
  /** Pfad je Datei-ID. */
  files: Map<string, { projectId: string; folderId: string; path: string; size: number }>;
}

/** Wo jedes Projekt, jeder Ordner und jede Datei im Ordner am PC liegt. Gleiche Namen bekommen eine Nummer. */
export function layout(manifest: SyncManifest): SyncLayout {
  const taken = new Set<string>();
  const result: SyncLayout = { projectDirs: new Map(), folders: new Map(), files: new Map() };
  const projects = [...manifest.projects].sort((a, b) => a.name.localeCompare(b.name, "de") || a.id.localeCompare(b.id));
  for (const project of projects) {
    const projectDir = unique(safeName(project.name), taken, "");
    result.projectDirs.set(key(projectDir), { projectId: project.id, path: projectDir });
    const folderPath = new Map<string, string>();
    const children = new Map<string | null, SyncFolder[]>();
    for (const f of project.folders) children.set(f.parentId, [...(children.get(f.parentId) ?? []), f]);
    const walk = (parentId: string | null, dir: string) => {
      const list = [...(children.get(parentId) ?? [])].sort((a, b) => a.name.localeCompare(b.name, "de") || a.id.localeCompare(b.id));
      for (const f of list) {
        const path = join(dir, unique(safeName(f.name), taken, dir));
        folderPath.set(f.id, path);
        result.folders.set(key(path), { projectId: project.id, folderId: f.id, path });
        walk(f.id, path);
      }
    };
    walk(null, projectDir);
    const files = [...project.files].sort((a, b) => a.createdAt.localeCompare(b.createdAt) || a.id.localeCompare(b.id));
    for (const file of files) {
      const dir = folderPath.get(file.folderId);
      if (dir === undefined) continue;
      const path = join(dir, unique(safeName(file.name), taken, dir));
      result.files.set(file.id, { projectId: project.id, folderId: file.folderId, path, size: file.size });
    }
  }
  return result;
}

/** Dateien, die Office, Windows oder macOS nebenbei anlegen, gehören nicht nach HandVero. */
export function ignoredLocal(path: string): boolean {
  if (key(path).startsWith(`${SYNC_DIR}/`) || key(path) === SYNC_DIR) return true;
  const name = basename(path);
  return /^(~\$|\.~|\._)/.test(name) || /\.(tmp|crdownload|part|partial)$/i.test(name) || /^(thumbs\.db|desktop\.ini|\.ds_store)$/i.test(name);
}

/** Bearbeitet? Größe anders oder Änderungszeit mehr als zwei Sekunden verschieden (FAT/Netzlaufwerke runden). */
function changed(local: LocalFile, known: SyncStateFile) {
  return local.size !== known.size || Math.abs(local.mtime - known.mtime) > 2000;
}

export function planSync(manifest: SyncManifest, state: SyncState, local: LocalListing): SyncPlan {
  const plan: SyncPlan = { actions: [], skipped: [] };
  const map = layout(manifest);
  const localFiles = new Map(local.files.filter((f) => !ignoredLocal(f.path)).map((f) => [key(f.path), f]));
  /** Pfade, die schon einer Datei aus HandVero zugeordnet sind und deshalb nicht hochgeladen werden. */
  const claimed = new Set<string>();
  const knownProjects = new Set(manifest.projects.map((p) => p.id));

  // Die Ordnerstruktur aus HandVero auch dann anlegen, wenn noch keine Dateien darin liegen.
  const localDirs = new Set(local.dirs.map(key));
  for (const d of [...map.projectDirs.values(), ...map.folders.values()]) {
    if (!localDirs.has(key(d.path))) plan.actions.push({ type: "mkdir", path: d.path });
  }

  for (const [fileId, remote] of map.files) {
    const known = state.files[fileId];
    const target = key(remote.path);
    const atTarget = localFiles.get(target);
    if (known) {
      const atKnown = localFiles.get(key(known.path));
      claimed.add(key(known.path));
      if (key(known.path) !== target) {
        // In HandVero umbenannt oder verschoben.
        if (atKnown && changed(atKnown, known)) {
          // Am PC zugleich bearbeitet: die bearbeitete Fassung bleibt, wo sie ist, und ersetzt die alte.
          const folder = map.folders.get(key(dirname(known.path)));
          if (folder) plan.actions.push({ type: "replace", fileId, projectId: folder.projectId, folderId: folder.folderId, path: atKnown.path, size: atKnown.size, mtime: atKnown.mtime });
          else plan.skipped.push({ path: atKnown.path, reason: "Die Datei wurde in HandVero verschoben und hier bearbeitet. Bitte in einen Projektordner legen." });
          continue;
        }
        if (atTarget) {
          claimed.add(target);
          plan.skipped.push({ path: remote.path, reason: "Hier liegt schon eine andere Datei mit diesem Namen." });
          continue;
        }
        claimed.add(target);
        if (atKnown) plan.actions.push({ type: "move", fileId, from: atKnown.path, to: remote.path });
        else plan.actions.push({ type: "download", fileId, projectId: remote.projectId, path: remote.path, size: remote.size });
        continue;
      }
      if (!atKnown) plan.actions.push({ type: "download", fileId, projectId: remote.projectId, path: remote.path, size: remote.size });
      else if (changed(atKnown, known)) {
        plan.actions.push({ type: "replace", fileId, projectId: remote.projectId, folderId: remote.folderId, path: atKnown.path, size: atKnown.size, mtime: atKnown.mtime });
      }
      continue;
    }
    claimed.add(target);
    if (!atTarget) plan.actions.push({ type: "download", fileId, projectId: remote.projectId, path: remote.path, size: remote.size });
    else if (atTarget.size === remote.size) {
      // Liegt schon da (z. B. nach einem neuen Einrichten des Abgleichs): nur merken.
      plan.actions.push({ type: "adopt", fileId, projectId: remote.projectId, path: atTarget.path, size: atTarget.size, mtime: atTarget.mtime });
    } else plan.skipped.push({ path: atTarget.path, reason: "In HandVero gibt es eine andere Datei mit diesem Namen. Bitte eine der beiden umbenennen." });
  }

  // In HandVero nicht mehr vorhanden.
  for (const [fileId, known] of Object.entries(state.files)) {
    if (map.files.has(fileId)) continue;
    const atKnown = localFiles.get(key(known.path));
    if (atKnown && !changed(atKnown, known) && knownProjects.has(known.projectId)) {
      claimed.add(key(known.path));
      plan.actions.push({ type: "trash", fileId, path: atKnown.path });
    } else {
      // Projekt archiviert oder nicht mehr freigegeben: die Kopie am PC bleibt liegen.
      if (atKnown && !knownProjects.has(known.projectId)) claimed.add(key(known.path));
      plan.actions.push({ type: "forget", fileId });
    }
  }

  // Neue Unterordner am PC. Je Abgleich eine Ebene; der nächste Durchgang legt die tieferen an.
  const wantedDirs = new Set<string>();
  for (const d of local.dirs) if (!ignoredLocal(d)) wantedDirs.add(d);
  for (const f of localFiles.values()) if (!claimed.has(key(f.path))) wantedDirs.add(dirname(f.path));
  const planned = new Set<string>();
  for (const dir of wantedDirs) {
    if (!dir || map.folders.has(key(dir)) || map.projectDirs.has(key(dir))) continue;
    const parent = dirname(dir);
    const parentFolder = map.folders.get(key(parent));
    const parentProject = map.projectDirs.get(key(parent));
    if (!parentFolder && !parentProject) continue;
    if (planned.has(key(dir))) continue;
    planned.add(key(dir));
    plan.actions.push({
      type: "createFolder",
      projectId: (parentFolder ?? parentProject)!.projectId,
      parentId: parentFolder?.folderId ?? null,
      name: basename(dir).slice(0, 120),
      path: dir,
    });
  }

  // Neue Dateien am PC.
  for (const f of localFiles.values()) {
    if (claimed.has(key(f.path))) continue;
    const dir = dirname(f.path);
    const folder = map.folders.get(key(dir));
    if (!folder) {
      if (map.projectDirs.has(key(dir))) plan.skipped.push({ path: f.path, reason: "Dateien bitte in einen Unterordner des Projekts legen." });
      else if (!dir) plan.skipped.push({ path: f.path, reason: "Dateien bitte in einen Projektordner legen." });
      // Tiefer liegende neue Ordner kommen beim nächsten Durchgang an die Reihe.
      continue;
    }
    if (!ALLOWED_FILES[fileExtension(f.path)]) {
      plan.skipped.push({ path: f.path, reason: "Diese Dateiart nimmt HandVero nicht an." });
      continue;
    }
    if (f.size > MAX_FILE_MB * 1024 * 1024) {
      plan.skipped.push({ path: f.path, reason: `Größer als ${MAX_FILE_MB} MB.` });
      continue;
    }
    if (f.size === 0) continue;
    plan.actions.push({ type: "upload", projectId: folder.projectId, folderId: folder.folderId, path: f.path, size: f.size, mtime: f.mtime });
  }
  return plan;
}
